import { useState } from 'react';
import { Calendar, Loader2, FileText, DollarSign, Eye, Upload, Trash2, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function DocumentosPagoSection({ expedienteId, expediente, onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [showEnviarModal, setShowEnviarModal] = useState(false);
  const [montoCalculo, setMontoCalculo] = useState('');
  const [enviandoCalculo, setEnviandoCalculo] = useState(false);
  const [eliminandoCalculo, setEliminandoCalculo] = useState(false);

  const handleUploadCalculo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('calculo', file);
      const response = await fetch(
        `${BACKEND_URL}/api/expedientes/${expedienteId}/documentos-pago`,
        { method: 'POST', credentials: 'include', body: formData }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Cálculo cargado exitosamente');
        if (onUpdate) onUpdate();
      } else {
        toast.error(data.detail || 'Error al cargar cálculo');
      }
    } catch (error) {
      toast.error('Error de conexión al cargar cálculo');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleEnviarCalculo = async () => {
    if (!montoCalculo.trim()) {
      toast.error('Por favor ingresa el monto del retiro');
      return;
    }
    setEnviandoCalculo(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/expedientes/${expedienteId}/enviar-calculo`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ monto_retiro: montoCalculo })
        }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Cálculo enviado al cliente');
        setShowEnviarModal(false);
        setMontoCalculo('');
        if (onUpdate) onUpdate();
      } else {
        toast.error(data.detail || 'Error al enviar cálculo');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setEnviandoCalculo(false);
    }
  };

  const handleEliminarCalculo = async () => {
    if (!window.confirm('¿Estás seguro de eliminar el cálculo? Esta acción no se puede deshacer.')) return;
    setEliminandoCalculo(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/expedientes/${expedienteId}/calculo`,
        { method: 'DELETE', credentials: 'include' }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || 'Cálculo eliminado');
        if (onUpdate) onUpdate();
      } else {
        toast.error(data.detail || 'Error al eliminar cálculo');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setEliminandoCalculo(false);
    }
  };
  
  const calculo = expediente?.calculo;

  return (
    <Card className="bg-gray-800/50 border-gray-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-500" />
          Cálculo de Reembolso
        </CardTitle>
        <CardDescription className="text-gray-400">
          Visualización del cálculo de reembolso del expediente
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mostrar cálculo si existe */}
        {calculo?.documento_url ? (
          <div className="space-y-3">
            <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
              <p className="text-green-300 font-semibold mb-3">✓ Cálculo Cargado</p>
              
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-gray-900/50 p-3 rounded">
                  <p className="text-gray-400 text-xs">Monto Total</p>
                  <p className="text-white font-bold text-lg">
                    ${calculo.monto_total?.toLocaleString('es-VE', {minimumFractionDigits: 2, maximumFractionDigits: 2}) || 'N/A'}
                  </p>
                </div>
                <div className="bg-gray-900/50 p-3 rounded">
                  <p className="text-gray-400 text-xs">Calculado por</p>
                  <p className="text-white text-sm">{calculo.calculado_por || 'N/A'}</p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(calculo.documento_url, '_blank')}
                  className="flex-1 text-blue-400 border-blue-500/30 hover:bg-blue-500/10"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Ver
                </Button>
              
                <Button
                  size="sm"
                  onClick={() => setShowEnviarModal(true)}
                  className="flex-1 bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/30"
                  data-testid="enviar-calculo-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Enviar
                </Button>

                <Button
                  size="sm"
                  onClick={handleEliminarCalculo}
                  disabled={eliminandoCalculo}
                  className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
                  data-testid="eliminar-calculo-btn"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {eliminandoCalculo ? 'Eliminando...' : 'Eliminar'}
                </Button>
              </div>
            </div>
            
            {/* Declaración firmada */}
            {expediente?.firma_url && (
              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3">
                <p className="text-green-300 text-sm font-semibold mb-2">
                  ✓ Declaración Jurada Firmada Recibida
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(expediente.firma_url, '_blank')}
                  className="text-blue-400 border-blue-500/30 hover:bg-blue-500/10"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Ver Declaración Firmada
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4 space-y-3">
            <p className="text-yellow-300 text-sm">
              No se ha cargado el cálculo de reembolso aún
            </p>
            <div>
              <Label className="text-gray-300 text-sm mb-1 block">Subir Cálculo de Reembolso (PDF o Imagen)</Label>
              <Input
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                onChange={handleUploadCalculo}
                disabled={uploading}
                className="bg-gray-700 border-gray-600 text-white file:bg-green-600 file:text-white file:border-0 file:rounded file:px-3 file:py-1 file:mr-3 file:cursor-pointer"
              />
              {uploading && (
                <div className="flex items-center gap-2 mt-2 text-green-300 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Subiendo cálculo y enviando al cliente...
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Modal para enviar cálculo con monto */}
        {showEnviarModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setShowEnviarModal(false)}>
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-white font-semibold text-lg mb-4">Enviar Cálculo al Cliente</h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-300 mb-1 block">Monto del Retiro *</Label>
                  <Input
                    type="text"
                    placeholder="Ej: $5,000.00"
                    value={montoCalculo}
                    onChange={(e) => setMontoCalculo(e.target.value)}
                    disabled={enviandoCalculo}
                    className="bg-gray-700 border-gray-600 text-white"
                    data-testid="monto-calculo-input"
                  />
                </div>
                <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-blue-300 text-xs">
                    Se enviará un correo al cliente con el monto calculado y las instrucciones para firmar la declaración jurada.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setShowEnviarModal(false)}
                    disabled={enviandoCalculo}
                    variant="outline"
                    className="flex-1 border-gray-600 text-gray-300"
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleEnviarCalculo}
                    disabled={enviandoCalculo || !montoCalculo.trim()}
                    className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black"
                    data-testid="confirmar-enviar-calculo-btn"
                  >
                    <Loader2 className={`w-4 h-4 mr-2 ${enviandoCalculo ? 'animate-spin' : 'hidden'}`} />
                    <Send className={`w-4 h-4 mr-2 ${enviandoCalculo ? 'hidden' : ''}`} />
                    <span>{enviandoCalculo ? 'Enviando...' : 'Enviar'}</span>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function FechaPagoSection({ expedienteId, expediente, onUpdate }) {
  const [fechaPago, setFechaPago] = useState('');
  const [montoRetiro, setMontoRetiro] = useState('');
  const [cuentaBancaria, setCuentaBancaria] = useState('');
  const [enviando, setEnviando] = useState(false);

  const handleEnviarFecha = async () => {
    if (!fechaPago) {
      toast.error('Por favor selecciona una fecha');
      return;
    }

    setEnviando(true);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/expedientes/${expedienteId}/fecha-pago`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fecha_pago: fechaPago,
            monto_retiro: montoRetiro || null,
            cuenta_bancaria: cuentaBancaria || null
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || 'Pago registrado exitosamente');
        setFechaPago('');
        setMontoRetiro('');
        setCuentaBancaria('');
        if (onUpdate) onUpdate();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al registrar pago');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error de conexión');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Card className="bg-gray-800/50 border-gray-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Calendar className="w-5 h-5 text-purple-500" />
          Fecha de Pago
        </CardTitle>
        <CardDescription className="text-gray-400">
          Registrar fecha estimada de pago al cliente
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Mostrar fecha estimada si existe */}
        {expediente?.fecha_pago_estimada && (
          <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-3 mb-3">
            <p className="text-purple-300 text-sm">
              Fecha estimada: {(() => {
                const f = expediente.fecha_pago_estimada;
                // Si es YYYY-MM-DD (10 chars), parsear sin timezone
                if (f.length === 10 && f.includes('-')) {
                  const [y, m, d] = f.split('-');
                  return `${parseInt(d)}/${parseInt(m)}/${y}`;
                }
                // Si es ISO con T, usar parseo seguro con mediodía para evitar desfase
                const dt = new Date(f.includes('T') ? f : f + 'T12:00:00');
                return dt.toLocaleDateString('es-VE', { timeZone: 'America/Caracas' });
              })()}
            </p>
          </div>
        )}

        {/* Mostrar fecha real si existe */}
        {expediente?.fecha_pago_real && (
          <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 mb-3">
            <p className="text-green-300 text-sm font-semibold">
              ✓ Pago realizado: {(() => {
                const f = expediente.fecha_pago_real;
                if (f.length === 10 && f.includes('-')) {
                  const [y, m, d] = f.split('-');
                  return `${parseInt(d)}/${parseInt(m)}/${y}`;
                }
                const dt = new Date(f.includes('T') ? f : f + 'T12:00:00');
                return dt.toLocaleDateString('es-VE', { timeZone: 'America/Caracas' });
              })()}
            </p>
          </div>
        )}

        {/* Formulario para registrar pago */}
        <div className="space-y-3">
          <div>
            <Label className="text-gray-300 mb-1 block">Monto de Retiro</Label>
            <Input
              type="text"
              placeholder="Ej: 5,000.00"
              value={montoRetiro}
              onChange={(e) => setMontoRetiro(e.target.value)}
              disabled={enviando}
              className="bg-gray-700 border-gray-600 text-white"
            />
          </div>
          <div>
            <Label className="text-gray-300 mb-1 block">Cuenta Bancaria Destino</Label>
            <Input
              type="text"
              placeholder="Ej: 0102-1234-56-7890123456"
              value={cuentaBancaria}
              onChange={(e) => setCuentaBancaria(e.target.value)}
              disabled={enviando}
              className="bg-gray-700 border-gray-600 text-white"
            />
          </div>
          <div>
            <Label className="text-gray-300 mb-1 block">Fecha de Pago</Label>
            <Input
              type="date"
              value={fechaPago}
              onChange={(e) => setFechaPago(e.target.value)}
              disabled={enviando}
              className="bg-gray-700 border-gray-600 text-white"
            />
          </div>
        </div>

        <Button
          onClick={handleEnviarFecha}
          disabled={enviando || !fechaPago}
          className="w-full bg-purple-500 hover:bg-purple-600 text-white"
          data-testid="registrar-fecha-pago-btn"
        >
          <Loader2 className={`w-4 h-4 mr-2 ${enviando ? 'animate-spin' : 'hidden'}`} />
          <Calendar className={`w-4 h-4 mr-2 ${enviando ? 'hidden' : ''}`} />
          <span>{enviando ? 'Registrando...' : 'Registrar Fecha de Pago'}</span>
        </Button>
      </CardContent>
    </Card>
  );
}
